import { saveSignupIntent, readSignupIntent, registerCopy } from "@/lib/signupIntent";
import { useState, useEffect } from "react";
import { returnToFrom, rememberReturnTo, takeReturnTo } from "@/lib/returnTo";
import LoginSlides, { BRICKS_SLIDES } from "../components/auth/LoginSlides";
import { fetchOAuthProviderEnabled, GOOGLE_UNAVAILABLE_MESSAGE } from "@/lib/webAccount";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "next-themes";
import api, { formatApiErrorDetail } from "@/lib/api";
import { supabase, setRememberMe } from "@/lib/supabase";
import { T, F, I, Over, Wordmark, th as palette } from "@/lib/bricks";
import { passkeyUserMessage, webAuthnAvailable } from "@/lib/passkeys";
import { DEMO_MODE } from "@/lib/demoMode";
import { fetchPhoneProviderEnabled } from "@/lib/phoneAuth";
import { lockedReason } from "@/lib/legalDocs";
import LegalDocSheet from "@/components/legal/LegalDocSheet";
import { LegalDocRows, useLegalReads } from "@/components/legal/LegalDocRows";
import PhoneAuthPanel from "@/components/PhoneAuthPanel";

const REGISTER_DOCS = ["terms", "privacy"];

const inputStyle = (p) => ({
  marginTop:8, width:'100%', padding:'14px 16px',
  border:`1px solid ${p.hairS}`, borderRadius:2,
  fontSize:15, fontFamily:F.display, color:p.ink,
  background:'transparent', outline:'none', boxSizing:'border-box',
});

// Offices and agents sign in here. Photographers use Dispatch after login.
const AUTH_CALLBACK = "https://app.brxs.ca/auth/callback";
// Hide until Apple Services ID is pasted into Supabase Auth (docs/sign-in-with-apple.md).
const APPLE_SIGNIN_ENABLED = false;


function PhoneMark({ color }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.2" stroke={color} strokeWidth="1.6"/>
      <path d="M10.5 18h3" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  );
}

function PasskeyMark({ color }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="8" r="3.2" stroke={color} strokeWidth="1.6"/>
      <path d="M12 11.2v4.2" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
      <path d="M9.2 18.2c.7-1.4 1.7-2.1 2.8-2.1s2.1.7 2.8 2.1" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
      <circle cx="12" cy="12" r="9.2" stroke={color} strokeWidth="1.6"/>
    </svg>
  );
}

export default function LoginPage() {
  const [mode, setMode] = useState(() => { const m = new URLSearchParams(window.location.search).get("mode"); return m === "register" || m === "forgot" ? m : "login"; }); // login | register | forgot
  // www hands off ?type= (account type to highlight in onboarding) and, from a
  // /book/site booking, ?email= so the new account matches the booking.
  const [email, setEmail] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    saveSignupIntent(window.location.search);
    const e = (q.get("email") || "").trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) && e.length <= 254 ? e : "";
  });
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(true);
  const legal = useLegalReads(REGISTER_DOCS);
  const [termsChecked, setTermsChecked] = useState(false);
  const [termsWhy, setTermsWhy] = useState(false);
  const termsAccepted = legal.done && termsChecked;
  const [passkeyOk, setPasskeyOk] = useState(false);
  // null = unknown (show button; provider-disabled error is handled), false = hidden.
  const [phoneEnabled, setPhoneEnabled] = useState(null);
  const [phoneMode, setPhoneMode] = useState(false);
  const { login, loginWithPasskey, loginWithSession, register, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';

  useEffect(() => {
    setPasskeyOk(webAuthnAvailable());
  }, []);

  // Hide "Continue with phone" when Supabase reports external.phone === false.
  useEffect(() => {
    if (DEMO_MODE) { setPhoneEnabled(false); return undefined; }
    let alive = true;
    fetchPhoneProviderEnabled(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY)
      .then((enabled) => { if (alive) setPhoneEnabled(enabled); });
    return () => { alive = false; };
  }, []);

  // Where to land after any sign-in: the deep link the visitor was sent here from.
  const dest = returnToFrom(location);
  const goToDest = () => { takeReturnTo(); navigate(dest, { replace: true }); };

  useEffect(() => {
    if (authLoading || !user) return;
    takeReturnTo();
    navigate(dest, { replace: true });
  }, [authLoading, user, navigate, dest]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get("error");
    if (err) {
      setError(err);
      params.delete("error");
      const qs = params.toString();
      window.history.replaceState({}, "", qs ? "/login?" + qs : "/login");
    }
  }, []);

  const p = palette(dark);

  const submitBlocked = loading || (mode === "register" && !termsAccepted);
  const switchMode = (next) => { setMode(next); setError(""); setInfo(""); setPhoneMode(false); };

  const handlePhoneSession = async (session) => {
    await loginWithSession(session);
    goToDest();
  };

  const handleGoogle = async () => {
    setError("");
    // Disabled provider = Supabase answers /authorize with raw 400 JSON; say it here instead.
    const googleOn = await fetchOAuthProviderEnabled("google", process.env.REACT_APP_SUPABASE_URL || "", process.env.REACT_APP_SUPABASE_ANON_KEY || "");
    if (googleOn === false) { setError(GOOGLE_UNAVAILABLE_MESSAGE); return; }
    setRememberMe(remember);
    rememberReturnTo(dest); // AuthCallback picks it up after the provider round trip
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: AUTH_CALLBACK,
        queryParams: { access_type: "offline", prompt: "select_account" },
      },
    });
    if (oauthError) {
      setError(oauthError.message || "Google sign-in is not available.");
    }
  };

  // Same PKCE callback as Google. Does not complete until Kam creates a Services ID
  // and key in Apple Developer and pastes them into Supabase Auth → Apple.
  const handleApple = async () => {
    setError("");
    setRememberMe(remember);
    rememberReturnTo(dest); // AuthCallback picks it up after the provider round trip
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "apple",
      options: {
        redirectTo: AUTH_CALLBACK,
        scopes: "name email",
      },
    });
    if (oauthError) {
      const raw = oauthError.message || "";
      const low = raw.toLowerCase();
      if (
        low.includes("provider is not enabled")
        || low.includes("unsupported provider")
        || low.includes("validation_failed")
      ) {
        setError("Sign in with Apple is not connected yet. Use Google, a passkey, or email.");
      } else {
        setError(raw || "Apple sign-in is not available.");
      }
    }
  };

  const handlePasskey = async () => {
    setError("");
    setInfo("");
    setRememberMe(remember);
    setLoading(true);
    try {
      await loginWithPasskey();
      goToDest();
    } catch (err) {
      const msg = passkeyUserMessage(err, "sign-in");
      if (msg) setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    setLoading(true);
    try {
      if (mode === "login") {
        setRememberMe(remember);
        await login(email, password);
        goToDest();
      } else if (mode === "register") {
        if (!termsAccepted) {
          if (!legal.done) setTermsWhy(true);
          setError(legal.done ? "Accept the terms and privacy policy to continue." : "");
          setLoading(false);
          return;
        }
        const result = await register(email, password, name, true);
        if (result.confirm_email) {
          setInfo("Account created — check your email to confirm before signing in.");
        } else {
          navigate("/", { replace: true });
        }
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setInfo("If that email is registered, a reset link is on its way.");
      }
    } catch (err) {
      const raw = formatApiErrorDetail(err.response?.data?.detail) || err.message || "";
      const clean = raw.toLowerCase().includes("password")
        ? "Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character."
        : raw;
      setError(clean);
    } finally { setLoading(false); }
  };

  const heading = {
    login:    'Welcome back.',
    register: 'Create your account.',
    forgot:   'Reset your password.',
  }[mode];

  const overline = { login:'Sign in', register:'Create account', forgot:'Forgot password' }[mode];
  const submitLabel = { login:'Sign in', register:'Create account', forgot:'Send reset link' }[mode];
  const showPasskey = mode === 'login' && passkeyOk && !info;
  // ?type= from www (saved on first render) picks the line and the first photo.
  const copy = registerCopy(readSignupIntent());
  const slides = [BRICKS_SLIDES[copy.slide], ...BRICKS_SLIDES.filter((_, k) => k !== copy.slide)];

  return (
    <div data-testid="login-page" style={{ display:'flex', minHeight:'100vh', background:p.bg, fontFamily:F.display, color:p.ink }}>

      {/* Left — form */}
      <div className="px-5 py-8 md:px-14 md:py-12" style={{ flex:1, borderRight:`1px solid ${p.hair}`, display:'flex', flexDirection:'column' }}>
        <style>{`
          .brand-home-btn svg rect{transform-origin:center;}
          .brand-home-btn:hover svg rect:nth-child(1){animation:brickPop .5s cubic-bezier(.16,1,.3,1) forwards;}
          .brand-home-btn:hover svg rect:nth-child(2){animation:brickPop .5s cubic-bezier(.16,1,.3,1) .07s forwards;}
          .brand-home-btn:hover svg rect:nth-child(3){animation:brickPop .5s cubic-bezier(.16,1,.3,1) .14s forwards;}
          @keyframes brickPop{0%{transform:translateY(0);}40%{transform:translateY(-3px);}100%{transform:translateY(0);}}
        `}</style>
        <button
          type="button"
          onClick={() => { window.location.href = "https://brxs.ca"; }}
          aria-label="Bricks — go to home"
          className="brand-home-btn cursor-pointer"
          style={{ background: 'none', border: 'none', padding: 0, minHeight: 44, display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start' }}
        >
          <Wordmark size={19} color={p.ink} />
        </button>

        <div style={{ marginTop:'auto', paddingTop:48, maxWidth:440 }}>
          <Over color={p.ink4}>{overline}</Over>
          <div style={{ fontSize:44, fontWeight:500, letterSpacing:-1.4, marginTop:12, lineHeight:1.05 }}>
            {heading}
          </div>
          {mode === 'register' && (
            <p data-testid="register-tagline" style={{ fontSize:15, lineHeight:1.5, color:p.ink3, margin:'14px 0 0', maxWidth:400 }}>{copy.line}</p>
          )}

          <div style={{ marginTop:32, display:'flex', flexDirection:'column', gap:16 }}>

            {phoneMode && mode !== 'forgot' && (
              <PhoneAuthPanel p={p} mode={mode} remember={remember}
                onSession={handlePhoneSession}
                onBack={() => setPhoneMode(false)} />
            )}

            {!phoneMode && (<>
            {/* Name (register only) */}
            {mode === 'register' && (
              <div>
                <Over color={p.ink3}>Name</Over>
                <input data-testid="register-name-input" type="text" value={name}
                  onChange={e => setName(e.target.value)} placeholder="Full name" required style={inputStyle(p)} />
              </div>
            )}

            {/* Email */}
            <div>
              <Over color={p.ink3}>Email</Over>
              <input data-testid="login-email-input" type="email" value={email}
                onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required style={inputStyle(p)} />
            </div>

            {/* Password (login + register only) */}
            {mode !== 'forgot' && (
              <div>
                <Over color={p.ink3}>Password</Over>
                <input data-testid="login-password-input" type="password" value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="Enter password" required
                  style={inputStyle(p)} />
              </div>
            )}

            {/* Remember me (login only) */}
            {mode === 'login' && (
              <label style={{ display:'flex', alignItems:'center', gap:10, minHeight:44, cursor:'pointer', userSelect:'none' }}>
                <input data-testid="remember-me-checkbox" type="checkbox" checked={remember}
                  onChange={e => setRemember(e.target.checked)}
                  style={{ width:18, height:18, accentColor:p.ink, cursor:'pointer' }} />
                <span style={{ fontSize:13, color:p.ink3 }}>Stay signed in on this device</span>
              </label>
            )}

            {/* Terms (register only): required, unticked by default. The server records when it was accepted. */}
            {mode === 'register' && (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <LegalDocRows docs={REGISTER_DOCS} read={legal.read} onOpen={legal.setOpen} colors={p} />
                <label onClick={() => { if (!legal.done) setTermsWhy(true); }}
                  style={{ display:'flex', alignItems:'center', gap:10, minHeight:44, cursor:legal.done ? 'pointer' : 'not-allowed', userSelect:'none' }}>
                  <input data-testid="register-terms-checkbox" type="checkbox" checked={termsAccepted} aria-disabled={!legal.done}
                    aria-describedby={termsWhy && !legal.done ? 'register-terms-why' : undefined}
                    onChange={e => { if (!legal.done) { setTermsWhy(true); return; } setTermsChecked(e.target.checked); }}
                    style={{ width:18, height:18, flexShrink:0, accentColor:p.ink, cursor:legal.done ? 'pointer' : 'not-allowed', opacity:legal.done ? 1 : 0.4 }} />
                  <span style={{ fontSize:13, color:p.ink3 }}>I agree to the terms of service and the privacy policy.</span>
                </label>
                {termsWhy && !legal.done && <p id="register-terms-why" role="status" data-testid="register-terms-why" style={{ fontSize:13, color:p.ink2, margin:0, lineHeight:1.45 }}>{lockedReason(REGISTER_DOCS, legal.read)}</p>}
                <LegalDocSheet doc={legal.open} read={legal.open ? !!legal.read[legal.open] : false} onRead={legal.markRead} onClose={() => legal.setOpen(null)} colors={p} />
              </div>
            )}

            {/* Feedback */}
            {error && <p data-testid="auth-error" style={{ fontSize:13, color:T.bad, margin:0 }}>{error}</p>}
            {info  && <p style={{ fontSize:13, color:T.ok, margin:0 }}>{info}</p>}

            {/* Submit */}
            {!info && (
              <button data-testid="login-submit-btn" type="submit" disabled={submitBlocked} onClick={handleSubmit}
                style={{ marginTop:8, padding:'13px 20px', background:T.brick, color:'#fff', border:'none', borderRadius:2, fontSize:14, fontWeight:500, fontFamily:F.display, cursor:submitBlocked ? 'not-allowed' : 'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:8, width:'100%', opacity:submitBlocked ? 0.6 : 1 }}
              >
                {loading ? '…' : submitLabel}
                {!loading && <I name="arr" size={14} color="#fff"/>}
              </button>
            )}
            </>)}

            {/* Passkey, Google, Apple — login + register. OAuth uses /auth/callback. */}
            {mode !== 'forgot' && !info && !phoneMode && (
              <>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ flex:1, height:1, background:p.hair }}/>
                  <span style={{ fontSize: 12, color:p.ink4 }}>or</span>
                  <div style={{ flex:1, height:1, background:p.hair }}/>
                </div>
                {showPasskey && (
                  <button type="button" data-testid="login-passkey-btn" onClick={handlePasskey} disabled={loading}
                    style={{ padding:'13px 20px', background:'transparent', color:p.ink, border:`1px solid ${p.hair}`, borderRadius:2, fontSize:14, fontWeight:500, fontFamily:F.display, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:10, width:'100%', opacity:loading ? 0.6 : 1 }}
                  >
                    <PasskeyMark color={p.ink} />
                    Continue with passkey
                  </button>
                )}
                <button type="button" data-testid="login-google-btn" onClick={handleGoogle}
                  style={{ padding:'13px 20px', background:'transparent', color:p.ink, border:`1px solid ${p.hair}`, borderRadius:2, fontSize:14, fontWeight:500, fontFamily:F.display, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:10, width:'100%' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </button>
                {phoneEnabled !== false && (
                <button type="button" data-testid="login-phone-btn" onClick={() => { setError(""); setInfo(""); setPhoneMode(true); }}
                  style={{ padding:'13px 20px', background:'transparent', color:p.ink, border:`1px solid ${p.hair}`, borderRadius:2, fontSize:14, fontWeight:500, fontFamily:F.display, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:10, width:'100%' }}
                >
                  <PhoneMark color={p.ink} />
                  Continue with phone
                </button>
                )}
                {APPLE_SIGNIN_ENABLED && (
                <button type="button" data-testid="login-apple-btn" onClick={handleApple}
                  style={{ padding:'13px 20px', background:'transparent', color:p.ink, border:`1px solid ${p.hair}`, borderRadius:2, fontSize:14, fontWeight:500, fontFamily:F.display, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:10, width:'100%' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M16.365 1.43c0 1.14-.433 2.197-1.226 3.014-.83.86-2.185 1.52-3.32 1.43-.147-1.09.42-2.23 1.2-3.08C13.89 1.85 15.25 1.2 16.365 1.43zM20.48 17.34c-.55 1.27-.81 1.84-1.52 2.96-1 1.56-2.4 3.5-4.14 3.51-1.55.02-1.95-1.01-4.06-1-2.1.01-2.54 1.02-4.08 1-1.75-.02-3.08-1.77-4.08-3.33-2.8-4.37-3.09-9.5-1.36-12.22 1.22-1.92 3.15-3.04 4.96-3.04 1.85 0 3.02 1.02 4.56 1.02 1.5 0 2.41-1.03 4.56-1.03 1.62 0 3.34.88 4.56 2.4-4.01 2.2-3.36 7.93.6 9.43z"/>
                  </svg>
                  Continue with Apple
                </button>
                )}
              </>
            )}

            {/* Footer links */}
            <div style={{ fontSize:12, color:p.ink3, marginTop:4, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              {mode === 'login' && (
                <button onClick={() => switchMode('forgot')}
                  style={{ background:'none', border:'none', cursor:'pointer', color:p.ink, textDecoration:'underline', textUnderlineOffset:3, fontSize:12, padding:0, minHeight:44, fontFamily:F.display }}>
                  Forgot password?
                </button>
              )}
              {mode === 'forgot' && (
                <button onClick={() => switchMode('login')}
                  style={{ background:'none', border:'none', cursor:'pointer', color:p.ink, textDecoration:'underline', textUnderlineOffset:3, fontSize:12, padding:0, minHeight:44, fontFamily:F.display }}>
                  ← Back to sign in
                </button>
              )}
              <span style={{ marginLeft:'auto' }}>
                {mode === 'login'    && <>No account? <button onClick={() => switchMode('register')} style={{ background:'none', border:'none', cursor:'pointer', color:p.ink, textDecoration:'underline', textUnderlineOffset:3, fontSize:12, padding:0, minHeight:44, fontFamily:F.display }}>Sign up</button></>}
                {mode === 'register' && <>Have an account? <button onClick={() => switchMode('login')} style={{ background:'none', border:'none', cursor:'pointer', color:p.ink, textDecoration:'underline', textUnderlineOffset:3, fontSize:12, padding:0, minHeight:44, fontFamily:F.display }}>Sign in</button></>}
              </span>
            </div>
          </div>
        </div>

      </div>

      {/* Right — calm rotation of real Bricks work, one line each. Hidden below lg. */}
      <div className="hidden lg:block" style={{ flex:'0 0 clamp(380px, 42vw, 760px)', position:'relative', overflow:'hidden', background:'#1a1612' }}>
        <LoginSlides slides={slides} />
      </div>

    </div>
  );
}
